import useTableDataDinamic from '@/hooks/useTableDataDinamic'
import { UTCDateToCountryDate } from '@/utils/countryDate.util'
import { currencyFormat } from '@/utils/currencyFormat'
import { StatusColor } from '@/utils/StatusColor.utility'
import { badgeStyle } from '@/utils/reuseBrand'
import { sbuIcon } from '@/utils/sbuIcon'
import { Chip, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Skeleton, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import DoubleScrollTable from '@/components/ui/DoubleScrollTable'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { MdCloudDownload, MdOutlineSearch, MdClose } from 'react-icons/md'
import CustomDateRangePicker from '../CustomDateRangePicker/CustomDateRangePicker'
import { useSelector } from 'react-redux'
import { IConfigSeller } from '@/models/configSeller'

const columns = [
  { name: '#', uid: 'id' },
  { name: 'Dispositivo', uid: 'sbu' },
  { name: 'Marca', uid: 'brand' },
  { name: 'Modelo', uid: 'model' },
  { name: 'Imei/Serie', uid: 'serie' },
  { name: 'Estado', uid: 'status' },
  { name: 'Tienda', uid: 'store' },
  { name: 'Fecha de entrega', uid: 'fecha_entregado' },
  { name: 'En custodia por', uid: 'custody_name' },
  { name: 'Fecha de custodia', uid: 'fecha_custodia' },
  { name: 'Retirado por', uid: 'delivered_name' },
  { name: 'Fecha Retirado', uid: 'fecha_retirado' },
  { name: 'Sello', uid: 'seal' },
  { name: 'Sello2', uid: 'seal2' },
  { name: 'Sello3', uid: 'seal3' },
  { name: 'Peso caja', uid: 'seal_weight' }
]

const excelName = 'Listado_TradeIn_Custodia'

const TableRetirados = () => {
  const [showCustomDateModal, setShowCustomDateModal] = useState(false)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [toolbarEl, setToolbarEl] = useState<HTMLElement | null>(null)
  const {
    sortDescriptor,
    filterValue,
    sortedItems,
    bottomContent,
    onSearchChange,
    setSortDescriptor,
    loading,
    onClear,
    loadingExcel,
    exportToCSV
  } = useTableDataDinamic(columns.concat([{ name: 'Tienda', uid: 'store' }]), excelName, 'retirados', [7])
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const isHuaweiMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_mx'
  const isWomChile = configSeller.config.s3.FOLDER_APP === 'ti_wom'
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'
  const isHuaweiOnlineMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'
  const isHuaweiOnlinePeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe'
  const isReuseChile = configSeller.config.s3.FOLDER_APP === 'ti_reusecl'
  const isConectaPeru = configSeller.config.s3.FOLDER_APP === 'ti_conecta_peru'
  const isRipleyPeru = configSeller.config.s3.FOLDER_APP === 'ti_ripley_pe'
  const isHuaweiPeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_pe'

  useEffect(() => {
    setToolbarEl(document.getElementById('table-toolbar'))
  }, [])

  const renderCell: any = (item: any, columnKey: any) => {
    const cellValue = item[columnKey]

    /* eslint default-case: "error" */
    switch (columnKey) {
      case 'discount_amount':
        return (<span className='font-bold text-xs'>{currencyFormat(Number(cellValue), import.meta.env.VITE_COUNTRY)}</span>)
      case 'name':
        return (<span className='font-bold text-xs'>{item[columnKey] + ' ' + item.lastname}</span>)
      case 'status':
        return (<Chip size='sm' radius='md' style={badgeStyle(StatusColor(cellValue, true), 0.16)}>{cellValue}</Chip>)
      case 'fecha_entregado':
        return UTCDateToCountryDate(cellValue)
      case 'fecha_custodia':
        return UTCDateToCountryDate(cellValue)
      case 'fecha_retirado':
        return UTCDateToCountryDate(cellValue)
      case 'sbu':
        return (
          <div className='flex items-start gap-1.5'>
            <span className='text-slate-400 mt-0.5'>{sbuIcon(cellValue)}</span>
            <div className='flex flex-col leading-tight'>
              <span className='text-xs font-semibold text-slate-700'>{item.brand} {item.model}</span>
              <span className='text-tiny text-slate-400'>{cellValue}</span>
            </div>
          </div>
        )
      default:
        return (<span className='text-xs'>{cellValue}</span>)
    }
  }

  /* Marca y Modelo ya se muestran integrados en la celda "Dispositivo" (ver renderCell),
     así que se ocultan como columnas propias en pantalla — pero se mantienen en `columns`/
     la exportación a Excel, que sigue trayendo cada dato en su propia columna. */
  const visibleColumns = columns.filter(e => e.uid !== 'brand' && e.uid !== 'model')

  const handleExportCustomRange = () => {
    setShowCustomDateModal(false)
    exportToCSV('custom_range', customStart, customEnd)
    setCustomStart('')
    setCustomEnd('')
  }

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.3 }} className='space-y-4'
    >
      {toolbarEl && createPortal(
        <>
          <div className='relative'>
            <MdOutlineSearch className='pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={filterValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder='Buscar...'
              className='w-56 pl-8 pr-7'
            />
            {filterValue && (
              <button
                type='button'
                aria-label='Limpiar búsqueda'
                onClick={() => onClear()}
                className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
              >
                <MdClose />
              </button>
            )}
          </div>
          <Dropdown>
            <DropdownTrigger>
              <Button variant='secondary' disabled={loadingExcel}>
                <MdCloudDownload className={loadingExcel ? 'animate-spin' : ''} />
                Descargar
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label='Static Actions'>
              <DropdownItem key='copy' onPress={() => exportToCSV('last_week')}>Última semana</DropdownItem>
              <DropdownItem key='copy' onPress={() => exportToCSV('last_month')}>Últimos 30 días</DropdownItem>
              <DropdownItem key='edit' onPress={() => exportToCSV('current_month')}>Mes actual</DropdownItem>
              <DropdownItem key='edit' onPress={() => exportToCSV('previous_month')}>Mes anterior</DropdownItem>
              <DropdownItem key='delete' onPress={() => exportToCSV('all')}>Todo</DropdownItem>
              <DropdownItem isDisabled={isHuaweiMexico || isWomChile || isHuaweiOnlineChile || isHuaweiOnlineMexico || isHuaweiOnlinePeru || isReuseChile || isConectaPeru || isRipleyPeru || isHuaweiPeru} key='custom' onPress={() => setShowCustomDateModal(true)}>Rango Personalizado</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </>,
        toolbarEl
      )}
      <DoubleScrollTable>
        <Table
          removeWrapper
          aria-label='Example table with client side sorting'
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
          bottomContent={bottomContent}
          bottomContentPlacement='outside'
          classNames={{ th: 'bg-slate-100 text-slate-500 text-tiny' }}
        >
          <TableHeader columns={visibleColumns}>
            {(column) => (
              <TableColumn key={column.uid} align={column.uid === 'actions' ? 'center' : 'start'} allowsSorting={column.uid !== 'actions'} className={column.uid === 'nombre' || column.uid === 'actions' || column.uid === 'ordenCompraCodigo' ? '' : 'md:table-cell'}>
                {column.name}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody emptyContent={loading ? '' : 'Sin resultados'}>
            {
              loading
                ? Array.from({ length: 8 }, (_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {visibleColumns.map(column => (
                      <TableCell key={column.uid}>
                        <Skeleton className='rounded-md'><div className='h-4 w-full' /></Skeleton>
                      </TableCell>
                    ))}
                  </TableRow>
                  ))
                : sortedItems.map((item: any, rowIndex: number) => (
                  <TableRow key={item.id} className={rowIndex % 2 === 1 ? 'bg-[#f5f5f599]' : ''}>
                    {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
                  </TableRow>
                  ))
            }
          </TableBody>
        </Table>
      </DoubleScrollTable>
      <CustomDateRangePicker
        open={showCustomDateModal}
        onClose={() => setShowCustomDateModal(false)}
        onExport={handleExportCustomRange}
        start={customStart}
        end={customEnd}
        setStart={setCustomStart}
        setEnd={setCustomEnd}
      />
    </motion.div>
  )
}

export default TableRetirados
